import { useCallback, useEffect, useState } from "react";
import type { FC } from "react";
import ObjectId from "bson-objectid";
import type PlantModel from "Models/Plant";
import DeletePlantModal from "./DeletePlantModal";
import PlantDetailsModal from "./PlantDetailsModal";
import PlantModalForm from "./PlantModalForm";
import PlantOverrideSettingsModal from "./PlantOverrideSettingsModal";
import { usePlantStore } from "Store/PlantStore";
import { useVarietyStore } from "Store/VarietyStore";

interface PlantModalProps {
  setPlant?: (plant: PlantModel) => Promise<void>;
  isActive?: boolean;
  onClose?: () => void;
}

const sanitizeString = (input: string) => input.trim();

const firstNonEmptyString = (...values: Array<string | undefined>): string =>
  values.find((value) => value !== undefined && value?.length > 0) ?? "";

const PlantModal: FC<PlantModalProps> = ({
  setPlant,
  isActive = true,
  onClose,
}) => {
  const createPlantRequest = usePlantStore((state) => state.createPlant);
  const updatePlant = usePlantStore((state) => state.updatePlant);
  const fetchPlants = usePlantStore((state) => state.fetchPlants);
  const plants = usePlantStore((state) => state.plants);
  const plantsLoaded = usePlantStore((state) => state.plantsLoaded);
  const selectedPlantId = usePlantStore((state) => state.selectedPlantId);
  const show = usePlantStore((state) => state.plantModalOpen);
  const showEditModal = usePlantStore((state) => state.plantModalEdit);
  const openPlantModal = usePlantStore((state) => state.openPlantModal);
  const closePlantModal = usePlantStore((state) => state.closePlantModal);
  const plant = plants.find((item) => item._id === selectedPlantId);
  const isOpen = show && isActive;

  const [plantName, setPlantName] = useState(plant?.name);
  const [plantShadePreference, setPlantShadePreference] = useState(
    plant?.shadePreference,
  );
  const [plantPlantingInfo, setPlantPlantingInfo] = useState(
    plant?.plantingInfo,
  );
  const [plantCompanionPlants, setPlantCompanionPlants] = useState<string[]>(
    plant?.companionPlants ?? [],
  );
  const [plantDaysToGerminate, setPlantDaysToGerminate] = useState(
    plant?.daysToGerminate ?? undefined,
  );
  const [plantDaysToMaturity, setPlantDaysToMaturity] = useState(
    plant?.daysToMaturity ?? undefined,
  );
  const [plantSeedDepth, setPlantSeedDepth] = useState(plant?.seedDepth);
  const [plantSeedDepthUnit, setPlantSeedDepthUnit] = useState(
    plant?.seedDepthUnit,
  );
  const [plantSeedSpacing, setPlantSeedSpacing] = useState(plant?.seedSpacing);
  const [plantSeedSpacingUnit, setPlantSeedSpacingUnit] = useState(
    plant?.seedSpacingUnit,
  );
  const [plantLightRequiredToGerminate, setPlantLightRequiredToGerminate] =
    useState(plant?.lightRequiredToGerminate);
  const [plantTemperatureTolerance, setPlantTemperatureTolerance] = useState(
    plant?.temperatureTolerance,
  );
  const [plantWaterRequirement, setPlantWaterRequirement] = useState(
    plant?.waterRequirement,
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateVariety, setShowCreateVariety] = useState(false);
  const [showCreateFromExistingPlant, setShowCreateFromExistingPlant] =
    useState(false);
  const [parentPlantId, setParentPlantId] = useState(plant?.parentPlantId);
  const [showOverridePlantSettings, setShowOverridePlantSettings] =
    useState(false);

  const fetchVarieties = useVarietyStore((state) => state.fetchVarieties);
  const varieties = useVarietyStore((state) => state.varieties);
  const variety = varieties.find((item) => item._id === plant?.varietyId);
  const createVariety = useVarietyStore((state) => state.createVariety);
  const updateVariety = useVarietyStore((state) => state.updateVariety);

  const [showSaveValidation, setShowSaveValidation] = useState(false);
  const [varietyNameOverride, setVarietyNameOverride] = useState<
    { plantId?: string; value: string } | undefined
  >();
  const varietyName =
    varietyNameOverride && varietyNameOverride.plantId === plant?._id
      ? varietyNameOverride.value
      : (variety?.name ?? "");
  const setVarietyName = (name: string) =>
    setVarietyNameOverride({ plantId: plant?._id, value: name });

  useEffect(() => {
    void fetchVarieties();
  }, [fetchVarieties]);

  useEffect(() => {
    void fetchPlants();
  }, [fetchPlants]);

  const closeModal = () => {
    setShowSaveValidation(false);
    setShowCreateVariety(false);
    setShowCreateFromExistingPlant(false);
    setShowOverridePlantSettings(false);
    setParentPlantId(undefined);
    clearPlant();
    closePlantModal();
    onClose?.();
  };

  const hideModal = () => {
    clearPlant();
    closeModal();
  };

  const updatePlantInfo = useCallback((plant?: PlantModel) => {
    if (!plant) return;
    setPlantName(plant.name);
    setPlantShadePreference(plant.shadePreference);
    setPlantPlantingInfo(plant.plantingInfo);
    setPlantCompanionPlants(plant.companionPlants ?? []);
    setPlantDaysToGerminate(plant.daysToGerminate);
    setPlantDaysToMaturity(plant.daysToMaturity);
    setPlantSeedDepth(plant.seedDepth);
    setPlantSeedSpacing(plant.seedSpacing);
    setPlantLightRequiredToGerminate(plant.lightRequiredToGerminate);
    setPlantTemperatureTolerance(plant.temperatureTolerance);
    setPlantWaterRequirement(plant.waterRequirement);
  }, []);

  const onCreateVariety = () => {
    if (plant?.name ?? plantName) {
      setVarietyName(plant?.name ?? plantName ?? "");

      setPlantName("");
    }

    setShowCreateVariety(true);
  };

  const clearPlant = () => {
    setPlantName("");
    setPlantShadePreference("");
    setPlantPlantingInfo("");
    setPlantCompanionPlants([]);
    setPlantDaysToGerminate(undefined);
    setPlantDaysToMaturity(undefined);
    setPlantSeedDepth(undefined);
    setPlantSeedSpacing(undefined);
    setPlantLightRequiredToGerminate(false);
    setParentPlantId(undefined);
    setPlantTemperatureTolerance("");
    setPlantWaterRequirement("");
    setShowCreateFromExistingPlant(false);
    setShowCreateVariety(false);
    setVarietyNameOverride(undefined);
  };

  const parentPlant = plants.find((item) => item._id === parentPlantId);
  const isCreateVarietyValid =
    !showCreateVariety ||
    (Boolean(parentPlant) && varietyName.trim().length >= 1);

  const save = async () => {
    const normalizedPlantName = plantName?.trim().toLowerCase();
    const plantNames = plants
      .filter(
        (currentPlant) =>
          currentPlant._id !== selectedPlantId && !currentPlant?.varietyId,
      )
      .map((item) => item.name.trim().toLowerCase());
    const plantIncluded =
      normalizedPlantName !== undefined &&
      normalizedPlantName.length > 0 &&
      plantNames.includes(normalizedPlantName);
    const isPlantNameChanged =
      !plant || normalizedPlantName !== plant.name.trim().toLowerCase();

    if (
      isPlantNameChanged &&
      plantIncluded &&
      !plant?.varietyId &&
      !variety?._id &&
      varietyName.length == 0
    ) {
      alert("A plant with this name already exists.");
      return;
    }

    if (!isCreateVarietyValid) {
      setShowSaveValidation(true);
      return;
    }

    let newPlant: PlantModel;

    if (varietyName.length > 1) {
      setPlantName(varietyName);
      if (plant && parentPlant) {
        plant.name = parentPlant.name;
      }
    }

    if (plant) {
      newPlant = {
        name: sanitizeString(
          firstNonEmptyString(plantName, plant.name, parentPlant?.name),
        ),
        shadePreference: sanitizeString(
          firstNonEmptyString(plantShadePreference, plant?.shadePreference),
        ),
        plantingInfo: sanitizeString(
          firstNonEmptyString(plantPlantingInfo, plant?.plantingInfo),
        ),
        companionPlants: plantCompanionPlants ?? plant?.companionPlants ?? [],
        daysToGerminate: plantDaysToGerminate ?? plant?.daysToGerminate,
        daysToMaturity: plantDaysToMaturity ?? plant?.daysToMaturity,
        seedDepth: plantSeedDepth ?? plant?.seedDepth,
        seedDepthUnit: plantSeedDepthUnit ?? plant.seedDepthUnit,
        seedSpacing: plantSeedSpacing ?? plant?.seedSpacing,
        seedSpacingUnit: plantSeedSpacingUnit ?? plant?.seedSpacingUnit,
        lightRequiredToGerminate:
          plantLightRequiredToGerminate ??
          plant?.lightRequiredToGerminate ??
          false,
        temperatureTolerance: sanitizeString(
          firstNonEmptyString(
            plantTemperatureTolerance,
            plant?.temperatureTolerance,
          ),
        ),
        waterRequirement: sanitizeString(
          firstNonEmptyString(plantWaterRequirement, plant?.waterRequirement),
        ),
        _id: plant._id,
        varietyId: variety?._id,
        parentPlantId: parentPlantId ?? parentPlant?._id,
      };

      await updatePlant(newPlant);
    } else {
      newPlant = {
        name: sanitizeString(firstNonEmptyString(plantName, parentPlant?.name)),
        shadePreference: sanitizeString(plantShadePreference ?? ""),
        plantingInfo: sanitizeString(plantPlantingInfo ?? ""),
        companionPlants: plantCompanionPlants,
        daysToGerminate: plantDaysToGerminate,
        daysToMaturity: plantDaysToMaturity,
        seedDepth: plantSeedDepth,
        seedDepthUnit: plantSeedDepthUnit,
        seedSpacing: plantSeedSpacing,
        seedSpacingUnit: plantSeedSpacingUnit,
        lightRequiredToGerminate: plantLightRequiredToGerminate,
        temperatureTolerance: sanitizeString(plantTemperatureTolerance ?? ""),
        waterRequirement: sanitizeString(plantWaterRequirement ?? ""),
        varietyId: variety?._id,
        _id: ObjectId().toHexString(),
        parentPlantId: parentPlant?._id,
      };

      if (!newPlant.name) return alert("A name is required to save the plant");
      newPlant = await createPlantRequest(newPlant);
    }

    if (varietyName) {
      const newVariety = {
        name: varietyName,
        plantId: newPlant._id,
        _id: newPlant.varietyId ?? ObjectId().toHexString(),
      };

      const savedVariety = newPlant?.varietyId
        ? await updateVariety(newVariety)
        : await createVariety(newVariety);

      newPlant.varietyId = savedVariety?._id ?? newPlant.varietyId;

      await updatePlant?.(newPlant);
    }
    await fetchPlants(true);
    await fetchVarieties(true);

    await setPlant?.(newPlant);

    closeModal();
  };

  useEffect(() => {
    if (!plant?._id && showEditModal) clearPlant();

    updatePlantInfo?.(plant);
  }, [plant, showEditModal, show, updatePlantInfo]);

  return (
    <div>
      {showEditModal ? (
        <PlantModalForm
          isOpen={isOpen && showEditModal}
          statusMessage={
            selectedPlantId && !plant
              ? plantsLoaded
                ? "Plant not found."
                : "Loading..."
              : undefined
          }
          plant={plant}
          plants={plants}
          varieties={varieties}
          variety={variety}
          parentPlant={parentPlant}
          parentPlantId={parentPlantId}
          selectedPlantId={selectedPlantId}
          plantName={plantName}
          varietyName={varietyName}
          showCreateVariety={showCreateVariety}
          showCreateFromExistingPlant={showCreateFromExistingPlant}
          showSaveValidation={showSaveValidation}
          plantCompanionPlants={plantCompanionPlants}
          plantPlantingInfo={plantPlantingInfo}
          plantDaysToGerminate={plantDaysToGerminate}
          plantDaysToMaturity={plantDaysToMaturity}
          plantSeedDepth={plantSeedDepth}
          plantSeedDepthUnit={plantSeedDepthUnit}
          plantSeedSpacing={plantSeedSpacing}
          plantSeedSpacingUnit={plantSeedSpacingUnit}
          plantLightRequiredToGerminate={plantLightRequiredToGerminate}
          plantTemperatureTolerance={plantTemperatureTolerance}
          plantWaterRequirement={plantWaterRequirement}
          onRequestClose={hideModal}
          onCancel={() => {
            clearPlant();
            closeModal();
          }}
          onSave={() => void save()}
          onPlantNameChange={setPlantName}
          onVarietyNameChange={setVarietyName}
          onCreateVariety={() => onCreateVariety()}
          onCreateFromExistingPlant={() => setShowCreateFromExistingPlant(true)}
          onParentPlantChange={(selectedPlant) => {
            setParentPlantId(selectedPlant._id);
            setShowOverridePlantSettings(true);
          }}
          onCreatePlant={() => openPlantModal(undefined, true)}
          onCompanionPlantsChange={setPlantCompanionPlants}
          onShadePreferenceChange={setPlantShadePreference}
          onPlantingInfoChange={setPlantPlantingInfo}
          onDaysToGerminateChange={setPlantDaysToGerminate}
          onDaysToMaturityChange={setPlantDaysToMaturity}
          onSeedDepthChange={setPlantSeedDepth}
          onSeedDepthUnitChange={setPlantSeedDepthUnit}
          onSeedSpacingChange={setPlantSeedSpacing}
          onSeedSpacingUnitChange={setPlantSeedSpacingUnit}
          onLightRequiredToGerminateChange={setPlantLightRequiredToGerminate}
          onTemperatureToleranceChange={setPlantTemperatureTolerance}
          onWaterRequirementChange={setPlantWaterRequirement}
        />
      ) : (
        <PlantDetailsModal
          isOpen={isOpen && !showEditModal}
          plant={plant}
          plants={plants}
          varieties={varieties}
          companionPlantIds={plantCompanionPlants}
          onClose={hideModal}
          onEdit={() => {
            updatePlantInfo?.(plant);
            openPlantModal(plant?._id, true);
          }}
          onDelete={() => {
            if (!plant) return;
            setShowDeleteModal(true);
          }}
        />
      )}
      <PlantOverrideSettingsModal
        isOpen={showOverridePlantSettings}
        parentPlant={parentPlant}
        onClose={() => {
          setShowOverridePlantSettings(false);
        }}
        onOverride={() => {
          updatePlantInfo?.(parentPlant);
          setShowOverridePlantSettings(false);
        }}
      />
      {showDeleteModal && (
        <DeletePlantModal
          setShowDeleteModal={setShowDeleteModal}
          showDeleteModal={showDeleteModal}
          plantId={plant?._id}
        />
      )}
    </div>
  );
};

export default PlantModal;
